// components/MultipleTemplateSelector.tsx
import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import AddressAutocomplete from "./AddressAutocomplete";
import {
    Search, CheckCircle2, ArrowLeft, Layers, Home, Upload, X, Loader2,
    ImageIcon, CheckCircle, AlertCircle, DollarSign, Building2, Plus,
    MapPin, Globe, Sparkles,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import "@/styles/template-responsive.css";
import MultiTemplate, { MultiTemplateHandle } from "@/components/templates/MultiTemplate";

const MAX_IMAGE_SIZE = 10 * 1024 * 1024;

interface Template {
    id: number;
    name: string;
    component: React.ComponentType<any>;
    photoCount: number;
    rating: number;
    tag: string;
    uses: number;
    category: string;
}

interface Property {
    id: string;
    title: string;
    image: string;
    photos?: string[];
    price: number;
    address: string;
    bedrooms: number;
    bathrooms: number;
    area: number;
    description?: string;
    property_type?: string;
    status?: string;
    subject?: string;
    is_open_house?: boolean;
    city?: string;
    state?: string;
    website?: string;
    [key: string]: any;
}

interface PhotoItem { id: string; url: string; file?: File; }

interface NewPropertyData {
    bedrooms: string;
    bathrooms: string;
    sqft: string;
    price: string;
    address: string;
    website: string;
    photo: PhotoItem | null;
}

interface MultipleTemplateSelectorProps {
    templates?: Template[];
    savedProperties?: Property[];
    loadingProperties?: boolean;
    onBack: () => void;
    onContinue: (selectedTemplates: Template[], selectedProperties: Property[], newPropertiesData: NewPropertyData[]) => void;
    getFullUrl?: (url: string | null | undefined) => string;
    onPropertyAdd?: (newProperty: Property) => void;
    onPropertyDelete?: (propertyId: string) => void;
    onRefreshProperties?: () => void;
}

const MultipleTemplateSelector: React.FC<MultipleTemplateSelectorProps> = ({
    templates = [],
    savedProperties = [],
    loadingProperties = false,
    onBack,
    onContinue,
    getFullUrl: getFullUrlProp,
    onPropertyAdd,
    onPropertyDelete,
    onRefreshProperties,
}) => {
    const { toast } = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dropZoneRef = useRef<HTMLDivElement>(null);

    const getFullUrl = useCallback(
        (url: string | null | undefined): string => {
            if (getFullUrlProp) return getFullUrlProp(url);
            if (!url) return "";
            if (url.startsWith("http") || url.startsWith("blob:") || url.startsWith("data:")) return url;
            return url;
        },
        [getFullUrlProp]
    );

    // ---- State ----
    const [selectedTemplates, setSelectedTemplates] = useState<Template[]>([]);
    const [photoFilter, setPhotoFilter] = useState<"all" | number>("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedProperties, setSelectedProperties] = useState<Property[]>([]);
    const [showAddNewForm, setShowAddNewForm] = useState(false);
    const [newPropertyData, setNewPropertyData] = useState<NewPropertyData>({
        bedrooms: "", bathrooms: "", sqft: "", price: "", address: "", website: "", photo: null,
    });
    const [isDragging, setIsDragging] = useState(false);
    const [locallyAddedProperties, setLocallyAddedProperties] = useState<Property[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    // ---- AI Enhancement State ----
    const [enhanceDialogOpen, setEnhanceDialogOpen] = useState(false);
    const [enhanceDialogPhoto, setEnhanceDialogPhoto] = useState<PhotoItem | null>(null);
    const [enhancedPreviewUrl, setEnhancedPreviewUrl] = useState<string | null>(null);
    const [isEnhancingPreview, setIsEnhancingPreview] = useState(false);
    const [isPhotoEnhanced, setIsPhotoEnhanced] = useState(false);
    const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(null);
    const [originalLoaded, setOriginalLoaded] = useState(false);
    const [enhancedLoaded, setEnhancedLoaded] = useState(false);

    useEffect(() => { setOriginalLoaded(false); }, [originalImageUrl]);
    useEffect(() => { setEnhancedLoaded(false); }, [enhancedPreviewUrl]);

    const generateId = useCallback(() => `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, []);
    const safeTemplates = useMemo(() => templates || [], [templates]);
const templateRefs = useRef<Record<number | string, MultiTemplateHandle | null>>({});
    const uniqueSavedProperties = useMemo(() => {
        const seen = new Set<string>();
        return (savedProperties || []).filter((p) => {
            const id = String(p?.id ?? "");
            if (!id || seen.has(id)) return false;
            seen.add(id);
            return true;
        });
    }, [savedProperties]);

    const allProperties = useMemo(() => {
        const savedIds = new Set(uniqueSavedProperties.map(p => String(p.id)));
        const uniqueLocal = locallyAddedProperties.filter(p => !savedIds.has(String(p.id)));
        return [...uniqueLocal, ...uniqueSavedProperties];
    }, [locallyAddedProperties, uniqueSavedProperties]);

    const filteredTemplates = useMemo(() => {
        if (!safeTemplates.length) return [];
        return safeTemplates.filter((t) => {
            if (!t) return false;
            const matchesPhoto = photoFilter === "all" || t.photoCount === photoFilter;
            const matchesSearch = (t.name || "").toLowerCase().includes((searchQuery || "").toLowerCase());
            return matchesPhoto && matchesSearch;
        });
    }, [safeTemplates, photoFilter, searchQuery]);

    // ---- Template handlers ----
    const toggleTemplateSelection = useCallback((template: Template) => {
        if (!template) return;
        setSelectedTemplates(prev => prev.some(t => t.id === template.id) ? prev.filter(t => t.id !== template.id) : [...prev, template]);
    }, []);
    const isTemplateSelected = useCallback((id: number) => selectedTemplates.some(t => t.id === id), [selectedTemplates]);
    const selectAllTemplates = useCallback(() => setSelectedTemplates(filteredTemplates), [filteredTemplates]);
    const clearAllTemplateSelections = useCallback(() => setSelectedTemplates([]), []);

    // ---- Property handlers ----
    const togglePropertySelection = useCallback((property: Property) => {
        setSelectedProperties(prev => prev.some(p => p.id === property.id) ? prev.filter(p => p.id !== property.id) : [...prev, property]);
    }, []);
    const isPropertySelected = useCallback((id: string) => selectedProperties.some(p => p.id === id), [selectedProperties]);
    const selectAllProperties = useCallback(() => setSelectedProperties(allProperties), [allProperties]);
    const clearAllPropertySelections = useCallback(() => setSelectedProperties([]), []);

    const handleIntegerChange = useCallback((field: keyof NewPropertyData, value: string) => {
        if (value && !/^\d*$/.test(value)) return;
        setNewPropertyData(prev => ({ ...prev, [field]: value }));
    }, []);
    const handleFloatChange = useCallback((field: keyof NewPropertyData, value: string) => {
        if (value && !/^\d*\.?\d*$/.test(value)) return;
        if ((value.match(/\./g) || []).length > 1) return;
        setNewPropertyData(prev => ({ ...prev, [field]: value }));
    }, []);
    const handleTextChange = useCallback((field: keyof NewPropertyData, value: string) => {
        setNewPropertyData(prev => ({ ...prev, [field]: value }));
    }, []);

    // ---- File handlers ----
    const processFile = useCallback((file: File) => {
        if (!file) return;
        if (!/\.(jpe?g|png)$/i.test(file.name || "")) {
            toast({ title: "Not Supported", description: "Only JPG, JPEG, PNG allowed.", variant: "destructive", duration: 1000 });
            return;
        }
        if (!file.type.startsWith("image/")) {
            toast({ title: "Invalid File Type", description: "Not an image file.", variant: "destructive", duration: 1000 });
            return;
        }
        if (file.size > MAX_IMAGE_SIZE) {
            toast({ title: "File Too Large", description: "Max 5 MB allowed.", variant: "destructive", duration: 1000 });
            return;
        }
        if (newPropertyData.photo?.url) URL.revokeObjectURL(newPropertyData.photo.url);
        const url = URL.createObjectURL(file);
        setNewPropertyData(prev => ({ ...prev, photo: { id: generateId(), url, file } }));
        setIsPhotoEnhanced(false);
        toast({ title: "Photo Added", description: "Photo uploaded successfully!", duration: 1000 });
    }, [newPropertyData.photo, toast, generateId]);

    const handleFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) processFile(files[0]);
        event.target.value = "";
    }, [processFile]);

    const handleDragEnter = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }, []);
    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault(); e.stopPropagation();
        if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) setIsDragging(false);
    }, []);
    const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); }, []);
    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault(); e.stopPropagation(); setIsDragging(false);
        const files = e.dataTransfer.files;
        if (files && files.length > 0) processFile(files[0]);
    }, [processFile]);

    const removePhoto = useCallback(() => {
        if (newPropertyData.photo?.url) URL.revokeObjectURL(newPropertyData.photo.url);
        setNewPropertyData(prev => ({ ...prev, photo: null }));
        setIsPhotoEnhanced(false);
        toast({ title: "Photo Removed", duration: 1000 });
    }, [newPropertyData.photo, toast]);

    useEffect(() => {
        return () => {
            if (newPropertyData.photo?.file && newPropertyData.photo.url?.startsWith("blob:")) {
                URL.revokeObjectURL(newPropertyData.photo.url);
            }
        };
    }, []);

    // ---- AI Enhancement ----
    const handleEnhanceImage = useCallback(async (photo: PhotoItem) => {
        if (!photo.file || isPhotoEnhanced) return;
        const capturedUrl = photo.url;
        setOriginalImageUrl(capturedUrl);
        setEnhanceDialogPhoto({ ...photo });
        setEnhancedPreviewUrl(null);
        setIsEnhancingPreview(true);
        setEnhanceDialogOpen(true);
        try {
            const formData = new FormData();
            formData.append("image", photo.file);
            const response = await axiosInstance.post(endpoints.enhance_image, formData, { headers: { "Content-Type": "multipart/form-data" } });
            if (response.data?.success) {
                setEnhancedPreviewUrl(response.data.enhanced_image_data || response.data.enhanced_image);
            } else {
                throw new Error("Enhancement failed");
            }
        } catch (error: any) {
            toast({ title: "Enhancement Failed", description: error.response?.data?.error || "Please try again later.", variant: "destructive", duration: 1000 });
            setEnhanceDialogOpen(false);
            setEnhanceDialogPhoto(null);
            setOriginalImageUrl(null);
            setEnhancedPreviewUrl(null);
        } finally {
            setIsEnhancingPreview(false);
        }
    }, [isPhotoEnhanced, toast]);

    const handleApplyEnhancement = useCallback(async () => {
        if (!enhanceDialogPhoto || !enhancedPreviewUrl) return;
        try {
            const isDataUrl = enhancedPreviewUrl.startsWith("data:");
            const enhancedFile = isDataUrl
                ? (() => {
                    const [header, base64] = enhancedPreviewUrl.split(",");
                    const mimeMatch = header.match(/data:(.*);base64/);
                    const mime = mimeMatch ? mimeMatch[1] : "image/jpeg";
                    const binary = atob(base64);
                    const array = new Uint8Array(binary.length);
                    for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
                    return new File([array], `enhanced_${Date.now()}.jpg`, { type: mime });
                })()
                : await (async () => {
                    const response = await fetch(enhancedPreviewUrl);
                    const blob = await response.blob();
                    return new File([blob], `enhanced_${Date.now()}.jpg`, { type: blob.type || "image/jpeg" });
                })();

            const newBlobUrl = URL.createObjectURL(enhancedFile);
            if (newPropertyData.photo?.url?.startsWith("blob:")) URL.revokeObjectURL(newPropertyData.photo.url);
            setNewPropertyData(prev => ({ ...prev, photo: { id: prev.photo?.id || generateId(), url: newBlobUrl, file: enhancedFile } }));
            setIsPhotoEnhanced(true);
            toast({ title: "Image Enhanced ✨", description: "AI enhancement applied successfully!", duration: 1000 });
            setEnhanceDialogOpen(false);
            setEnhanceDialogPhoto(null);
            setOriginalImageUrl(null);
            setEnhancedPreviewUrl(null);
        } catch (error) {
            toast({ title: "Error", description: "Failed to apply enhancement. Please try again.", variant: "destructive" });
        }
    }, [enhanceDialogPhoto, enhancedPreviewUrl, newPropertyData.photo, toast, generateId]);

    const closeEnhancementDialog = useCallback(() => {
        setEnhanceDialogOpen(false);
        setEnhanceDialogPhoto(null);
        setOriginalImageUrl(null);
        setEnhancedPreviewUrl(null);
        setIsEnhancingPreview(false);
    }, []);

    // ==========================================
    // VALIDATION
    // ==========================================
    const getValidationItems = useCallback(() => {
        return [
            { label: "Bedrooms", isValid: !!newPropertyData.bedrooms },
            { label: "Bathrooms", isValid: !!newPropertyData.bathrooms },
            { label: "Sq. Ft.", isValid: !!newPropertyData.sqft },
            { label: "Price", isValid: !!newPropertyData.price },
            { label: "Address", isValid: !!newPropertyData.address.trim() },
            { label: "Photo", isValid: !!newPropertyData.photo },
            // { label: "Website", isValid: !!newPropertyData.website?.trim() },
            { label: "Website Link", isValid: isValidWebsiteUrl(newPropertyData.website) },
        ];
    }, [newPropertyData]);

    const isValidWebsiteUrl = useCallback((url?: string | null): boolean => {
        const trimmed = (url || "").trim();
        if (!trimmed) return false;
        const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
        const websitePattern = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/;
        return websitePattern.test(trimmed) || websitePattern.test(normalized);
    }, []);


    const isNewPropertyValid = useMemo(
        () => {
            return !!newPropertyData.bedrooms &&
                !!newPropertyData.bathrooms &&
                !!newPropertyData.sqft &&
                !!newPropertyData.price &&
                !!newPropertyData.address.trim() &&
                !!newPropertyData.photo &&
                // !!newPropertyData.website?.trim();
                isValidWebsiteUrl(newPropertyData.website);
        },
        [newPropertyData,isValidWebsiteUrl]
    );

    const savePropertyToBackend = useCallback(async (propertyData: NewPropertyData): Promise<Property | null> => {
        try {
            const formData = new FormData();
            const title = propertyData.address || `Property - $${parseInt(propertyData.price).toLocaleString()}`;
            formData.append("title", title);
            const addressParts = propertyData.address.split(",").map(s => s.trim());
            const streetAddress = addressParts[0] || propertyData.address;
            const city = addressParts[1] || "";
            const state = addressParts[2] || "";
            formData.append("address", streetAddress);
            if (city) formData.append("city", city);
            if (state) formData.append("state", state);
            const cleanPrice = parseInt(propertyData.price.replace(/[^0-9.-]+/g, ""), 10);
            if (!isNaN(cleanPrice)) formData.append("price", cleanPrice.toString());
            formData.append("bedrooms", propertyData.bedrooms);
            formData.append("bathrooms", propertyData.bathrooms);
            if (propertyData.sqft) formData.append("area", propertyData.sqft);
            if (propertyData.website?.trim()) {
                let website = propertyData.website.trim();
                if (!/^https?:\/\//i.test(website)) website = `https://${website}`;
                formData.append("website", website);
            }
            formData.append("property_type", "Residential");
            formData.append("status", "active");
            formData.append("description", `Property located at ${propertyData.address}`);
            if (propertyData.photo?.file) formData.append("image", propertyData.photo.file);

            const createResponse = await axiosInstance.post(endpoints.properties, formData, { headers: { "Content-Type": "multipart/form-data" } });
            const propertyId = createResponse.data.id || createResponse.data._id;
            const { data: serverData } = await axiosInstance.get(`${endpoints.properties}${propertyId}/`);
            const serverPhotos = Array.isArray(serverData.photos) ? serverData.photos : [];

            return {
                id: String(serverData.id || serverData._id),
                title: serverData.title || title,
                address: serverData.address || streetAddress,
                city: serverData.city || city,
                state: serverData.state || state,
                price: serverData.price || cleanPrice,
                bedrooms: serverData.bedrooms || parseInt(propertyData.bedrooms) || 0,
                bathrooms: serverData.bathrooms || parseInt(propertyData.bathrooms) || 0,
                area: serverData.area || parseInt(propertyData.sqft) || 0,
                property_type: serverData.property_type || "Residential",
                status: serverData.status || "active",
                image: getFullUrl(serverData.image),
                photos: serverPhotos.map((url: string) => getFullUrl(url)),
                is_open_house: serverData.is_open_house ?? false,
                subject: serverData.subject || "",
                description: serverData.description || "",
                website: serverData.website || propertyData.website || "",
            };
        } catch (error: any) {
            console.error("Error saving property:", error);
            return null;
        }
    }, [getFullUrl]);

    const handleAddNewPropertyToList = useCallback(async () => {

        if (!isValidWebsiteUrl(newPropertyData.website)) {
        toast({
            title: "Invalid Website URL",
            description: "Please enter a valid website URL (e.g., example.com or https://example.com)",
            variant: "destructive",
            duration: 1000
        });
        return;
        }


        if (!isNewPropertyValid) {
            toast({ title: "Missing Required Fields", description: "Please fill all required fields.", variant: "destructive", duration: 1000 });
            return;
        }
        setIsSaving(true);
        try {
            const savedProperty = await savePropertyToBackend(newPropertyData);
            if (savedProperty) {
                if (newPropertyData.photo?.url?.startsWith("blob:")) URL.revokeObjectURL(newPropertyData.photo.url);
                if (onPropertyAdd) onPropertyAdd(savedProperty);
                setLocallyAddedProperties(prev => [savedProperty, ...prev]);
                setSelectedProperties(prev => [...prev, savedProperty]);
                setNewPropertyData({ bedrooms: "", bathrooms: "", sqft: "", price: "", address: "", website: "", photo: null });
                setIsPhotoEnhanced(false);
                setShowAddNewForm(false);
                toast({ title: "✅ Property Added", description: "Your property has been saved and selected.", duration: 1000 });
            } else {
                toast({ title: "Save Failed", description: "Could not save property to server. Please try again.", variant: "destructive", duration: 1000 });
            }
        } catch (error: any) {
            toast({ title: "Error", description: error.response?.data?.message || "Failed to save property. Please try again.", variant: "destructive" });
        } finally {
            setIsSaving(false);
        }
    }, [
        newPropertyData,
        isNewPropertyValid,
        isValidWebsiteUrl,
        toast,
        savePropertyToBackend,
        onPropertyAdd,
    ]);

    const handleFinalContinue = useCallback(() => {
        if (selectedProperties.length === 0) {
            toast({ title: "No Properties Selected", description: "Please select at least one property to continue.", variant: "destructive", duration: 1000 });
            return;
        }
        const existingProperties: Property[] = [];
        const newPropertiesData: NewPropertyData[] = [];
        selectedProperties.forEach(property => {
            if ((property as any)._isNew) newPropertiesData.push((property as any)._originalData as NewPropertyData);
            else existingProperties.push(property);
        });
        try {
            sessionStorage.setItem("multiSelectedProperties", JSON.stringify(selectedProperties));
            sessionStorage.setItem("multiSelectedTemplates", JSON.stringify(selectedTemplates.map(t => t.id)));
        } catch (e) { }
        onContinue(selectedTemplates, selectedProperties, newPropertiesData);
    }, [selectedTemplates, selectedProperties, toast, onContinue]);

    const handleResetForm = useCallback(() => {
        if (newPropertyData.photo?.url) URL.revokeObjectURL(newPropertyData.photo.url);
        setNewPropertyData({ bedrooms: "", bathrooms: "", sqft: "", price: "", address: "", website: "", photo: null });
        setIsPhotoEnhanced(false);
    }, [newPropertyData.photo]);

    const scale = 0.3;
    const scalePercent = `${(1 / scale) * 100}%`;

    // =========================================
    // RENDER: Enhancement Dialog
    // =========================================
    const EnhancementDialog = useMemo(() => (
        <Dialog open={enhanceDialogOpen} onOpenChange={(open) => { if (!open) closeEnhancementDialog(); }}>
            <DialogContent className="w-[95vw] max-w-5xl max-h-[90dvh] overflow-y-auto p-0">
                <DialogHeader className="sticky top-0 z-10 bg-background border-b px-4 sm:px-6 py-3 sm:py-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 flex-shrink-0">
                            <Sparkles className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                            <DialogTitle className="text-base sm:text-xl font-semibold">AI Image Enhancement</DialogTitle>
                            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">Compare before and after AI enhancement</p>
                        </div>
                    </div>
                </DialogHeader>

                <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                    {isEnhancingPreview ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <div className="relative">
                                <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <Sparkles className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 animate-pulse" />
                                </div>
                            </div>
                            <p className="mt-5 text-base font-medium">Enhancing your image...</p>
                            <p className="mt-1 text-xs sm:text-sm text-muted-foreground">This may take a few seconds</p>
                            {originalImageUrl && (
                                <div className="mt-5 p-2 bg-muted rounded-lg">
                                    <img src={originalImageUrl} alt="Processing" className="w-28 h-20 object-cover rounded opacity-60" />
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* Before / After comparison grid */}
                            <div className="enhancement-compare-grid">
                                {/* Original */}
                                <div className="space-y-2 sm:space-y-3">
                                    <h3 className="font-semibold flex items-center gap-2 text-sm sm:text-base">
                                        <div className="h-3 w-3 rounded-full bg-gray-400" />
                                        Original Image
                                    </h3>
                                    <div className="relative rounded-xl overflow-hidden border-2 border-gray-300 bg-muted min-h-[160px]">
                                        {originalImageUrl ? (
                                            <>
                                                <img src={originalImageUrl} alt="Original" className="w-full h-auto max-h-[400px] object-contain" onLoad={() => setOriginalLoaded(true)} />
                                                <div className="absolute top-2 left-2 bg-gray-600 text-white px-2.5 py-1 rounded-full text-xs font-semibold">Before</div>
                                            </>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
                                                <AlertCircle className="h-12 w-12 mb-2 opacity-30" />
                                                <p className="text-xs">Original image not available</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Enhanced */}
                                <div className="space-y-2 sm:space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-semibold flex items-center gap-2 text-sm sm:text-base">
                                            <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse" />
                                            Enhanced Image
                                        </h3>
                                        <span className="bg-green-100 text-green-700 px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                                            <Sparkles className="h-3 w-3" />AI Enhanced
                                        </span>
                                    </div>
                                    <div className="relative rounded-xl overflow-hidden border-2 border-green-500 bg-muted shadow-lg shadow-green-500/20 min-h-[160px]">
                                        {enhancedPreviewUrl ? (
                                            <>
                                                <img src={enhancedPreviewUrl} alt="Enhanced" className="w-full h-auto max-h-[400px] object-contain" onLoad={() => setEnhancedLoaded(true)} />
                                                <div className="absolute top-2 left-2 bg-green-600 text-white px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5">
                                                    <Sparkles className="h-3 w-3" />After
                                                </div>
                                            </>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
                                                <AlertCircle className="h-12 w-12 mb-2 opacity-30" />
                                                <p className="text-xs">Enhancement preview not available</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Enhancement info box */}
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 sm:p-5">
                                <div className="flex items-start gap-3 sm:gap-4">
                                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 flex-shrink-0">
                                        <Sparkles className="h-4 w-4 text-blue-600" />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-blue-900 mb-2 text-sm sm:text-base">AI Enhancement Applied</h4>
                                        <div className="validation-grid text-xs sm:text-sm text-blue-700">
                                            {["Improved lighting & contrast", "Enhanced color accuracy", "Reduced noise & artifacts", "Sharpened details"].map(item => (
                                                <div key={item} className="flex items-center gap-2">
                                                    <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                                                    <span>{item}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-3 border-t">
                                <Button variant="outline" size="lg" onClick={closeEnhancementDialog} className="w-full sm:w-auto">Cancel</Button>
                                <Button
                                    size="lg"
                                    className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white w-full sm:w-auto"
                                    onClick={handleApplyEnhancement}
                                    disabled={!enhancedPreviewUrl}
                                >
                                    <Sparkles className="mr-2 h-4 w-4" />Apply Enhancement
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    ), [enhanceDialogOpen, originalImageUrl, enhancedPreviewUrl, isEnhancingPreview, originalLoaded, enhancedLoaded, handleApplyEnhancement, closeEnhancementDialog]);

    // =========================================
    // RENDER: Add New Property Form
    // =========================================
    const AddNewPropertyForm = useMemo(() => (
        <div className="space-y-5 p-3 sm:p-4 bg-muted/30 rounded-lg border border-border">
            {/* Property Details */}
            <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-border">
                    <Building2 className="h-5 w-5 text-primary" />
                    <h4 className="font-semibold">Property Details</h4>
                </div>

                {/* Address */}
                <div className="space-y-2">
                    <Label htmlFor="multi-address" className="text-sm font-medium">
                        Address <span className="text-red-500">*</span>
                    </Label>
                    <AddressAutocomplete
                        showLabel={false}
                        id="multi-address"
                        placeholder="Enter property address"
                        value={newPropertyData.address}
                        onChange={(value) => handleTextChange("address", value)}
                        className="h-11 bg-background"
                    />
                </div>

                {/* Price */}
                <div className="space-y-2">
                    <Label htmlFor="multi-price" className="text-sm font-medium">
                        Price <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            id="multi-price" type="text" maxLength={12} inputMode="numeric"
                            placeholder="Enter price" value={newPropertyData.price}
                            onChange={(e) => handleIntegerChange("price", e.target.value)}
                            className="h-11 pl-10 bg-background"
                        />
                    </div>
                </div>

                {/* Website */}
                <div className="space-y-2">
                    <Label htmlFor="multi-website" className="text-sm font-medium">
                        Website Link <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            id="multi-website" type="url" placeholder="https://www.example.com/property"
                            value={newPropertyData.website}
                            onChange={(e) => handleTextChange("website", e.target.value)}
                            className="h-11 pl-10 bg-background"
                        />
                    </div>
                    <p className="text-xs text-muted-foreground">Add a link to the property listing page or virtual tour</p>
                </div>

                {/* Bedrooms / Bathrooms / Sqft — responsive 1→3 cols */}
                <div className="new-property-grid">
                    {[
                        { id: "multi-bedrooms", label: "Bedrooms", emoji: "🛏️", field: "bedrooms" as const, handler: "int" },
                        { id: "multi-bathrooms", label: "Bathrooms", emoji: "🚿", field: "bathrooms" as const, handler: "float" },
                        { id: "multi-sqft", label: "Sq. Ft.", emoji: "📐", field: "sqft" as const, handler: "int" },
                    ].map(({ id, label, emoji, field, handler }) => (
                        <div key={id} className="space-y-2">
                            <Label htmlFor={id} className="text-sm font-medium">
                                {label} <span className="text-red-500">*</span>
                            </Label>
                            <div className="relative">
                                <Input
                                    id={id} type="text"
                                    maxLength={field === "bathrooms" ? 4 : field === "sqft" ? 8 : 3}
                                    inputMode="decimal" placeholder="0"
                                    value={newPropertyData[field]}
                                    onChange={(e) => handler === "float"
                                        ? handleFloatChange(field, e.target.value)
                                        : handleIntegerChange(field, e.target.value)
                                    }
                                    className="h-11 text-center pr-10 bg-background"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-lg">{emoji}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Photo Upload */}
            <div className="space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-border">
                    <div className="flex items-center gap-2">
                        <ImageIcon className="h-5 w-5 text-primary" />
                        <h4 className="font-semibold">Property Photo <span className="text-red-500">*</span></h4>
                    </div>
                    <span className={`text-sm font-medium ${newPropertyData.photo ? "text-green-600" : "text-muted-foreground"}`}>
                        {newPropertyData.photo ? "1/1" : "0/1"} photo
                    </span>
                </div>

                {!newPropertyData.photo ? (
                    <div
                        ref={dropZoneRef}
                        onDragEnter={handleDragEnter} onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave} onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        className={`relative cursor-pointer rounded-lg border border-dashed p-6 sm:p-8 bg-background transition-all duration-200
                            ${isDragging ? "border-primary bg-primary/5 scale-[1.02]" : "border-border hover:border-primary/50 hover:bg-muted/40"}`}
                    >
                        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileInputChange} className="hidden" />
                        <div className="flex flex-col items-center justify-center text-center">
                            <div className={`mb-3 rounded-full p-3 sm:p-4 transition-colors ${isDragging ? "bg-primary/10" : "bg-muted"}`}>
                                <Upload className={`h-6 w-6 sm:h-8 sm:w-8 ${isDragging ? "text-primary" : "text-muted-foreground"}`} />
                            </div>
                            <p className="text-sm sm:text-base font-medium">
                                {isDragging ? "Drop your photo here" : "Drag & drop photo here"}
                            </p>
                            <p className="mt-1 text-xs sm:text-sm text-muted-foreground">
                                or <span className="font-medium text-primary">click to browse</span>
                            </p>
                            <p className="mt-2 text-xs text-muted-foreground">PNG, JPG up to 5 MB</p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className={`group relative rounded-xl overflow-hidden border-2 shadow-lg transition-all
                            ${isPhotoEnhanced ? "border-green-500 ring-2 ring-green-500/30" : "border-primary ring-2 ring-primary/30"}`}>
                            <div className="absolute left-2 top-2 z-20 flex items-center gap-1 rounded-full bg-primary px-2 py-1 text-xs font-semibold text-primary-foreground shadow-lg">
                                <CheckCircle2 className="h-3 w-3" />Main Photo
                            </div>
                            {isPhotoEnhanced && (
                                <div className="absolute left-2 bottom-2 z-20 flex items-center gap-1 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 px-2 py-1 text-xs font-semibold text-white shadow-lg">
                                    <Sparkles className="h-3 w-3" />Enhanced
                                </div>
                            )}
                            <button
                                onClick={(e) => { e.stopPropagation(); removePhoto(); }}
                                className="absolute right-2 top-2 z-30 flex h-7 w-7 items-center justify-center rounded-full bg-red-500 text-white shadow-lg ring-2 ring-white/80 transition-all hover:bg-red-600 hover:scale-110 active:scale-95"
                            >
                                <X className="h-4 w-4" strokeWidth={2.5} />
                            </button>
                            <div className="aspect-video bg-muted">
                                <img src={newPropertyData.photo.url} alt="Property photo" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
                            </div>
                            {newPropertyData.photo.file && !isPhotoEnhanced && (
                                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-gradient-to-t from-black/80 via-black/50 to-transparent opacity-0 transition-all duration-300 group-hover:opacity-100">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); if (newPropertyData.photo) handleEnhanceImage(newPropertyData.photo); }}
                                        className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg hover:from-blue-600 hover:to-indigo-700 hover:scale-105 active:scale-95"
                                    >
                                        <Sparkles className="h-4 w-4" />✨ Auto IQ
                                    </button>
                                </div>
                            )}
                            {isPhotoEnhanced && <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 via-emerald-500 to-green-500" />}
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                                <Upload className="h-4 w-4 mr-2" />Replace Photo
                            </Button>
                            {newPropertyData.photo?.file && !isPhotoEnhanced && (
                                <Button
                                    variant="outline" size="sm"
                                    onClick={() => { if (newPropertyData.photo) handleEnhanceImage(newPropertyData.photo); }}
                                    className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 text-blue-700 hover:from-blue-100 hover:to-indigo-100"
                                >
                                    <Sparkles className="h-4 w-4 mr-2" />✨ Auto IQ Enhance
                                </Button>
                            )}
                            {isPhotoEnhanced && (
                                <Badge className="bg-green-100 text-green-800 border-green-200">
                                    <Sparkles className="h-3 w-3 mr-1" />AI Enhanced
                                </Badge>
                            )}
                        </div>

                        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileInputChange} className="hidden" />

                        {newPropertyData.photo?.file && !isPhotoEnhanced && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <Sparkles className="h-3 w-3 text-blue-500" />
                                Hover over the image or click "Auto IQ Enhance" to improve quality
                            </p>
                        )}
                    </div>
                )}
            </div>

            {/* Validation */}
            <div className={`rounded-lg border p-3 sm:p-4 transition-colors ${isNewPropertyValid ? "border-green-200 bg-green-50" : "border-border bg-background"}`}>
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                    <h4 className="font-medium text-primary text-sm sm:text-base">Validation Status</h4>
                    <span className={`text-xs sm:text-sm font-medium ${isNewPropertyValid ? "text-green-600" : "text-muted-foreground"}`}>
                        {getValidationItems().filter(i => i.isValid).length}/{getValidationItems().length} complete
                    </span>
                </div>
                <div className="validation-grid">
                    {getValidationItems().map((item, index) => (
                        <div key={index} className="flex items-center gap-2">
                            {item.isValid
                                ? <CheckCircle className="h-4 w-4 flex-shrink-0 text-green-600" />
                                : <AlertCircle className="h-4 w-4 flex-shrink-0 text-muted-foreground" />}
                            <span className={`text-xs sm:text-sm ${item.isValid ? "text-foreground" : "text-muted-foreground"}`}>{item.label}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Form actions */}
            <div className="flex flex-wrap justify-end gap-2 sm:gap-3">
                <Button variant="outline" onClick={handleResetForm} className="flex-1 sm:flex-none">Reset</Button>
                <Button variant="outline" onClick={() => { handleResetForm(); setShowAddNewForm(false); }} className="flex-1 sm:flex-none">Cancel</Button>
                <Button
                    onClick={handleAddNewPropertyToList}
                    disabled={!isNewPropertyValid || isSaving}
                    className="flex-1 sm:flex-none bg-primary text-primary-foreground hover:bg-primary/90"
                >
                    {isSaving ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                    ) : (
                        <><Plus className="h-4 w-4 mr-2" />Add Property</>
                    )}
                </Button>
            </div>
        </div>
    ), [
        newPropertyData, isDragging, isNewPropertyValid, isSaving, isPhotoEnhanced,
        handleIntegerChange, handleFloatChange, handleTextChange,
        handleDragEnter, handleDragOver, handleDragLeave, handleDrop,
        handleFileInputChange, removePhoto, handleResetForm,
        getValidationItems, handleAddNewPropertyToList, handleEnhanceImage,
    ]);

    // =========================================
    // RENDER: Properties List
    // =========================================
    const PropertiesListWithAddNew = useMemo(() => (
        <div className="space-y-4">
            {/* Header row */}
            <div className="selector-header-row">
                <Label className="text-sm sm:text-base font-medium flex items-center gap-2">
                    <Home className="h-5 w-5 text-primary" />
                    Select Properties
                </Label>
                <div className="action-chips w-full sm:w-auto">
                    <Badge variant="secondary" className="h-8 px-3 flex items-center text-xs">
                        {selectedProperties.length} selected
                    </Badge>
                    {allProperties.length > 0 && (
                        <>
                            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={selectAllProperties} disabled={allProperties.length === 0}>
                                Select All
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={clearAllPropertySelections} disabled={selectedProperties.length === 0}>
                                Clear
                            </Button>
                        </>
                    )}
                    {!showAddNewForm && (
                        <Button variant="outline" size="sm" onClick={() => setShowAddNewForm(true)} className="gap-2 h-8">
                            <Plus className="h-4 w-4" />Add New
                        </Button>
                    )}
                </div>
            </div>

            {showAddNewForm && (
                <div className="animate-in slide-in-from-top-2 duration-300">{AddNewPropertyForm}</div>
            )}

            {loadingProperties ? (
                <div className="flex items-center justify-center py-10">
                    <Loader2 className="h-7 w-7 animate-spin text-primary mr-3" />
                    <p className="text-muted-foreground text-sm">Loading properties...</p>
                </div>
            ) : allProperties.length === 0 && !showAddNewForm ? (
                <div className="text-center py-10 bg-muted/20 rounded-lg border-2 border-dashed border-muted">
                    <Home className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground text-sm mb-3">No properties found.</p>
                    <Button onClick={() => setShowAddNewForm(true)} className="gap-2">
                        <Plus className="h-4 w-4" />Add Your First Property
                    </Button>
                </div>
            ) : (
                <div className="property-scroll-list space-y-3 pr-0 sm:pr-2">
                    {allProperties.map((property) => {
                        if (!property) return null;
                        const isSelected = isPropertySelected(property.id);
                        const mainImage = property.image ? getFullUrl(property.image) : null;
                        const isNewlyAdded = (property as any)._isNew;

                        return (
                            <div
                                key={property.id}
                                onClick={() => togglePropertySelection(property)}
                                className={`group relative border border-border rounded-lg p-3 sm:p-4 bg-background transition-all duration-200 cursor-pointer
                                    ${isSelected ? "border-primary ring-2 ring-primary/30 shadow-md" : "hover:shadow-lg hover:border-primary/30"}`}
                            >
                                <div className="absolute top-3 left-3 z-10">
                                    <Checkbox
                                        checked={isSelected}
                                        onCheckedChange={() => togglePropertySelection(property)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="h-5 w-5 bg-white"
                                    />
                                </div>
                                {isSelected && (
                                    <div className="absolute top-3 right-3 z-10">
                                        <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                                    </div>
                                )}
                                {isNewlyAdded && (
                                    <div className="absolute top-3 right-10 sm:right-12 z-10">
                                        <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">New</Badge>
                                    </div>
                                )}

                                {/* Inner layout — responsive flex */}
                                <div className="property-card-inner">
                                    {mainImage ? (
                                        <div className="property-card-thumb flex-shrink-0">
                                            <img
                                                src={mainImage} alt={property.title || "Property"}
                                                className="w-full h-full object-cover"
                                                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                                            />
                                        </div>
                                    ) : (
                                        <div className="property-card-thumb flex items-center justify-center flex-shrink-0">
                                            <Home className="h-7 w-7 text-muted-foreground" />
                                        </div>
                                    )}

                                    <div className="flex-1 min-w-0">
                                        <h3 className="mb-1 text-sm sm:text-lg font-semibold text-primary truncate">
                                            {property.title || "Untitled Property"}
                                        </h3>
                                        {property.address && (
                                            <p className="mb-1.5 flex items-start gap-1 text-xs sm:text-sm text-muted-foreground break-anywhere">
                                                <MapPin className="h-3 w-3 flex-shrink-0 mt-0.5" />
                                                <span>{[property.address, property.city, property.state].filter(Boolean).join(", ")}</span>
                                            </p>
                                        )}
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-foreground">
                                            <span className="text-red-500 font-semibold">${(property.price || 0).toLocaleString()}</span>
                                            <span className="whitespace-nowrap">🛏️ {property.bedrooms || 0} bed</span>
                                            <span className="whitespace-nowrap">🚿 {property.bathrooms || 0} bath</span>
                                            <span className="whitespace-nowrap">📐 {property.area || 0} sqft</span>
                                            {property.website && (
                                                <span className="flex items-center gap-1 text-blue-600 whitespace-nowrap">
                                                    <Globe className="h-3 w-3" />Link
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    ), [
        allProperties, loadingProperties, selectedProperties, showAddNewForm, AddNewPropertyForm,
        getFullUrl, togglePropertySelection, isPropertySelected, selectAllProperties, clearAllPropertySelections,
    ]);

    // =========================================
    // RENDER: Template Selection Grid
    // =========================================
    const TemplateSelectionGrid = useMemo(() => (
        <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-border">
                <Layers className="h-5 w-5 text-primary" />
                <h4 className="font-semibold">Select Templates</h4>
            </div>

            {/* Filter row */}
            <div className="selector-header-row">
                <div className="filter-chips">
                    <div className="relative w-[160px] sm:w-[200px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            className="pl-9 h-9 text-sm"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    {(["all", 1, 2, 3, 4] as const).map((value) => (
                        <Button
                            key={String(value)}
                            size="sm"
                            onClick={() => setPhotoFilter(value)}
                            variant={photoFilter === value ? "default" : "outline"}
                            className="photo-filter-btn"
                        >
                            {value === "all" ? "All" : `${value}📷`}
                        </Button>
                    ))}
                </div>
                <div className="action-chips">
                    <Badge variant="secondary" className="h-8 px-3 flex items-center text-xs">
                        {selectedTemplates.length} selected
                    </Badge>
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={selectAllTemplates} disabled={filteredTemplates.length === 0}>All</Button>
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={clearAllTemplateSelections} disabled={selectedTemplates.length === 0}>Clear</Button>
                </div>
            </div>

            <div className="template-scroll-area pr-1">
                {filteredTemplates.length === 0 ? (
                    <div className="text-center py-8 bg-muted/20 rounded-lg border-2 border-dashed border-muted">
                        <Layers className="h-9 w-9 text-muted-foreground mx-auto mb-3" />
                        <p className="text-sm text-muted-foreground">No templates found.</p>
                    </div>
                ) : (
                    <div className="template-grid">
                        {filteredTemplates.map((template) => {
                            if (!template) return null;
                            const TemplateComponent = template.component;
                            const isSelected = isTemplateSelected(template.id);

                            return (
                                <Card
                                    key={template.id}
                                    onClick={() => toggleTemplateSelection(template)}
                                    className={`relative cursor-pointer border-2 transition-all ${isSelected ? "border-primary ring-2 ring-primary/30" : "hover:border-primary/50"}`}
                                >
                                    <CardContent className="p-0 flex flex-col">
                                        <div className="absolute top-1 left-1 z-30">
                                            <Checkbox checked={isSelected} onCheckedChange={() => toggleTemplateSelection(template)} className="h-4 w-4 bg-white" />
                                        </div>
                                        {isSelected && (
                                            <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/30">
                                                <CheckCircle2 className="h-9 w-9 text-green-500 drop-shadow-lg" />
                                            </div>
                                        )}
                                        <div className="relative h-[80px] overflow-hidden bg-white">
                                            {TemplateComponent && (
                                                <div
                                                    className="absolute inset-0 origin-top-left pointer-events-none select-none"
                                                    style={{ transform: `scale(${scale})`, width: scalePercent, height: scalePercent }}
                                                >
                                                    <TemplateComponent
                                                        property={{ id: 1, title: "Sample", image: "", photos: [], price: 500000, address: "123 St", bedrooms: 3, bathrooms: 2, area: 2000, description: "", propertyListType: { id: 1, list_name: "" } }}
                                                        keyFeatures={[]}
                                                        propertyLists={[]}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-1.5">
                                            <p className="text-[10px] font-semibold truncate">{template.name || "Template"}</p>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    ), [filteredTemplates, selectedTemplates, photoFilter, searchQuery, isTemplateSelected, toggleTemplateSelection, selectAllTemplates, clearAllTemplateSelections, scale, scalePercent]);

    // =========================================
    // MAIN RENDER
    // =========================================
    return (
        <div className="mx-auto w-full max-w-5xl px-3 sm:px-4 py-4 sm:py-6">
            {/* <h2 className="page-title text-primary mb-6 sm:mb-8">
                Select Multiple Templates & Properties
            </h2> */}

            <Card>
                <CardContent className="space-y-5 sm:space-y-6 pt-4 sm:pt-6">
                    {PropertiesListWithAddNew}
                    <hr className="border-border" />
                    {/* {TemplateSelectionGrid} */}
                </CardContent>
            </Card>

            {/* Footer nav */}
            <div className="mt-4 sm:mt-6 template-footer-actions">
                <Button variant="outline" onClick={onBack} className="w-full sm:w-auto">
                    <ArrowLeft className="mr-2 h-4 w-4" />Back
                </Button>
                <Button
                    disabled={selectedProperties.length === 0}
                    onClick={handleFinalContinue}
                    className={`w-full text-white sm:w-auto
                        ${selectedProperties.length > 0
                            ? "bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 hover:from-blue-500 hover:via-blue-600 hover:to-blue-700"
                            : "bg-blue-200 cursor-not-allowed"
                        }`}
                >
                    Continue
                </Button>
            </div>

            {EnhancementDialog}
        </div>
    );
};

export default MultipleTemplateSelector;